/**
 * Utility functions for handling image URLs and preventing invalid URL errors
 */

import { SyntheticEvent } from 'react';
import { StaticImageData } from 'next/image';

/**
 * Validates if a string is a valid URL
 * @param url - The URL string to validate
 * @returns boolean indicating if the URL is valid
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Gets a safe image source with fallback
 * @param imageUrl - The primary image URL or StaticImageData
 * @param fallbackUrl - The fallback image URL
 * @returns A safe image URL
 */
export function getSafeImageSrc(imageUrl: string | StaticImageData | null | undefined, fallbackUrl: string): string {
  if (!imageUrl) {
    return fallbackUrl;
  }
  
  // If it's a StaticImageData object, use its src property
  if (typeof imageUrl === 'object' && 'src' in imageUrl) {
    return imageUrl.src;
  }
  
  // If it's a string, validate it
  if (typeof imageUrl === 'string') {
    if (imageUrl.trim() === '') {
      return fallbackUrl;
    }
    
    // If it's a relative path (starts with /), it's safe to use
    if (imageUrl.startsWith('/')) {
      return imageUrl;
    }
    
    // If it's a data URL, it's safe to use
    if (imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    
    // If it's an external URL, validate it
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return isValidUrl(imageUrl) ? imageUrl : fallbackUrl;
    }
  }
  
  // Default to fallback for any other cases
  return fallbackUrl;
}

/**
 * Handles image load errors by setting a fallback image
 * @param event - The React synthetic event from the image
 * @param fallbackUrl - The fallback image URL
 */
export function handleImageError(event: SyntheticEvent<HTMLImageElement, Event>, fallbackUrl: string): void {
  const target = event.currentTarget;
  if (target && target.src !== fallbackUrl) {
    target.src = fallbackUrl;
  }
}

/**
 * Creates an image error handler function
 * @param fallbackUrl - The fallback image URL
 * @returns A function that can be used as an onError handler
 */
export function createImageErrorHandler(fallbackUrl: string) {
  return (event: SyntheticEvent<HTMLImageElement, Event>) => handleImageError(event, fallbackUrl);
}
